import DOMPurify from 'dompurify';

import {
  LINK_REL,
  IMAGE_SRC,
  LINK_HREF,
  LINK_TARGET,
  EDITOR_CLASS,
  ALLOWED_TAGS,
  LANGUAGE_CLASS,
  ATTRIBUTES_BY_TAG,
  ALIGN_DECLARATION,
} from './listingDescriptionWhitelist';

type Purifier = ReturnType<typeof DOMPurify>;
type SanitizerWindow = Parameters<typeof DOMPurify>[0];

/**
 * What a description may be before it is parsed at all: its length, and how deep its markup nests.
 *
 * The server renders a car's page for everyone, in one process, and it sanitizes the description
 * as part of that render. The DOM it works in (jsdom) takes time that grows with the square of the
 * nesting — two thousand tags inside one another are seconds, six thousand are a stopped server —
 * and a description is written by whoever has a dealer account. So the cost is bounded here, by
 * reading the text, before a parser is given the chance: past either limit the description is not
 * rendered at all. Nothing an editor produces comes near them: twenty thousand characters are six
 * or seven pages, and forty levels are a dozen lists inside one another. The browser applies the
 * same limits — the same function runs there — so the server's markup and the browser's agree.
 * Since api#275 the api refuses to store a description past either bound, with the same numbers,
 * so a dealer is told at the cabinet instead of finding the description missing here.
 */
export const DESCRIPTION_MAX_LENGTH = 20_000;
export const DESCRIPTION_MAX_DEPTH = 40;

/** Elements that have no content, and so no closing tag: they open nothing. */
const VOID_TAGS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'source', 'track', 'wbr']);

/** The start of a tag, opening or closing, as a parser takes it: `<` (or `</`) and a letter. */
const TAG = /<(\/?)([a-zA-Z][a-zA-Z0-9-]*)/g;

/**
 * How deep the markup of `html` nests, read off its text.
 *
 * Every opening tag goes one level in, and a closing tag comes back out only when it closes the
 * very element that is open. For markup that is closed properly that is the exact depth. A closing
 * tag that does not match is counted on the deep side, on purpose: a parser keeps elements open
 * across it (`<b><div></b>` leaves the `div` open, with a new `b` inside it; a stray `</i>` closes
 * nothing), so a count that trusted such a tag could be walked past any limit.
 *
 * It is blind to what it does not read as a tag, and there it counts less than a parser makes of
 * the markup: a closing tag inside a comment, a processing instruction or an attribute value, or
 * one whose opening name holds a character outside `[a-zA-Z0-9-]` — forty-five rounds of
 * `<b><!-- </b> -->` are one level here and forty-five to a parser. What is measured here is what
 * the api stored, and that holds none of those: the api's sanitizer drops comments and processing
 * instructions, escapes attribute values and never nests past forty levels (api#275). A description
 * put into the database past the api — a hand-edited row, a restored dump — is the one case this
 * count could read shallower than it is. The api counts with the same rules
 * (`listingDescriptionNesting`), blind spots included, so the two agree on what is too deep.
 * Stops counting once it is past `limit`.
 */
export const nestingOf = (html: string, limit: number = Number.POSITIVE_INFINITY): number => {
  const open: string[] = [];
  let deepest = 0;

  for (const [, closing, tag] of html.matchAll(TAG)) {
    const name = tag.toLowerCase();

    if (closing) {
      if (open[open.length - 1] === name) open.pop();
    } else if (!VOID_TAGS.has(name)) {
      open.push(name);

      if (open.length > deepest) {
        deepest = open.length;

        if (deepest > limit) return deepest;
      }
    }
  }

  return deepest;
};

const withinBounds = (html: string): boolean =>
  html.length <= DESCRIPTION_MAX_LENGTH && nestingOf(html, DESCRIPTION_MAX_DEPTH) <= DESCRIPTION_MAX_DEPTH;

let purifier: Purifier | null = null;
let providedWindow: SanitizerWindow | null = null;
/** The last description sanitized: a page asks twice — for the page itself, and for its words. */
let last: { html: string; container: HTMLElement | null } | null = null;

/**
 * The window the sanitizer works in where there is no browser: the server gives it one of its own
 * (jsdom — the DOM DOMPurify is supported on outside a browser; `entry.server.tsx`), so the page it
 * renders carries the description through the very same whitelist the browser applies.
 */
export const provideSanitizerWindow = (window: SanitizerWindow | null): void => {
  providedWindow = window;
  purifier = null;
  last = null;
};

const ALLOWED_ATTR = [...new Set(Object.values(ATTRIBUTES_BY_TAG).flat())];

/**
 * A DOMPurify instance of its own, so the hooks below apply to listing descriptions only and never
 * to another caller of the library.
 */
const getPurifier = (): Purifier | null => {
  if (purifier) {
    return purifier;
  }

  const host = providedWindow ?? (typeof window === 'undefined' ? null : window);

  if (!host) {
    return null;
  }

  const instance = DOMPurify(host);

  if (!instance.isSupported) {
    return null;
  }

  // DOMPurify decides which attributes exist at all; this narrows them per tag and per value.
  instance.addHook('uponSanitizeAttribute', (node, data) => {
    const tag = node.nodeName.toLowerCase();
    const value = data.attrValue;

    if (!(ATTRIBUTES_BY_TAG[tag] ?? []).includes(data.attrName)) {
      data.keepAttr = false;

      return;
    }

    switch (data.attrName) {
      case 'class': {
        const classes = value
          .split(/\s+/)
          .filter((name) => EDITOR_CLASS.test(name) || (tag === 'code' && LANGUAGE_CLASS.test(name)));

        if (classes.length === 0) {
          data.keepAttr = false;
        } else {
          data.attrValue = classes.join(' ');
        }

        break;
      }
      case 'style': {
        const align = ALIGN_DECLARATION.exec(value)?.[1];

        if (align) {
          data.attrValue = `text-align: ${align}`;
        } else {
          data.keepAttr = false;
        }

        break;
      }
      case 'href':
        data.keepAttr = LINK_HREF.test(value);
        break;
      case 'src':
        // DOMPurify lets `data:` through on images by default; the whitelist does not.
        data.keepAttr = IMAGE_SRC.test(value);
        break;
      default:
        break;
    }
  });

  instance.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeName === 'A') {
      node.setAttribute('target', LINK_TARGET);
      node.setAttribute('rel', LINK_REL);
    }
  });

  purifier = instance;

  return purifier;
};

const sanitize = (html: string): HTMLElement | null => {
  const instance = getPurifier();

  if (!instance) {
    return null;
  }

  const fragment = instance.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: LINK_HREF,
    RETURN_DOM_FRAGMENT: true,
  });

  fragment.querySelectorAll('img:not([src])').forEach((image) => image.remove());
  fragment.querySelectorAll('a:not([href])').forEach((link) => link.replaceWith(...Array.from(link.childNodes)));

  const container = fragment.ownerDocument.createElement('div');
  container.appendChild(fragment);

  return container;
};

/**
 * The description within the editor's whitelist, as an element of DOMPurify's own inert document:
 * nothing in it loads or runs. `null` without a description, without a DOM to sanitize in, for a
 * description past the bounds above — and for one the sanitizer failed on: a description is a
 * part of the page, and a part that cannot be rendered is left out, not allowed to take the page
 * with it. The element is read, never changed, by its callers: the last one is kept.
 */
const sanitized = (html: string | null | undefined): HTMLElement | null => {
  if (!html || !withinBounds(html)) {
    return null;
  }

  if (last?.html === html) {
    return last.container;
  }

  let container: HTMLElement | null = null;

  try {
    container = sanitize(html);
  } catch (failure) {
    console.error('A listing description could not be sanitized and is left out:', failure);
  }

  last = { html, container };

  return container;
};

/**
 * The description as the storefront renders it: only the editor's whitelist, with the shells a
 * rejected URL leaves behind removed (an image without `src` is dropped, a link without `href` is
 * unwrapped to its text). Without a DOM (no `window`) it renders nothing rather than the raw input.
 */
export const sanitizeListingDescription = (html: string | null | undefined): string =>
  sanitized(html)?.innerHTML ?? '';

/** Where one run of words ends and the next begins, though no space stands between them in the markup. */
const BREAKS = new Set(['P', 'BR', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE', 'HR']);

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

/** The text under `node`, in order, with a space after every element of `BREAKS`. As deep as the bound allows. */
const collectWords = (node: Node, words: string[]): void => {
  node.childNodes.forEach((child) => {
    if (child.nodeType === TEXT_NODE) {
      words.push(child.nodeValue ?? '');
    } else if (child.nodeType === ELEMENT_NODE) {
      collectWords(child, words);

      if (BREAKS.has(child.nodeName)) words.push(' ');
    }
  });
};

/**
 * The words of a description, without its markup — what a page says of the car to a search engine
 * and a link preview (`pageHead.ts`). Read off the sanitized tree, never off the stored string: a
 * tag the whitelist drops takes its text with it only where the whitelist says so.
 */
export const listingDescriptionText = (html: string | null | undefined): string => {
  const container = sanitized(html);

  if (!container) {
    return '';
  }

  const words: string[] = [];

  collectWords(container, words);

  return words.join('').replace(/\s+/g, ' ').trim();
};
